import { AnimatePresence } from "motion/react";
import React, { useEffect, useMemo } from "react";
import { useAnimationMode } from "../hooks/use_animation_mode";
import { useSearchQuery } from "../providers/search_query_provider";
import { useSearchRouting } from "../providers/search_routing_provider";
import { useConversationsSearch } from "../websockets/conversations/use_conversations_search";
import SearchResultsData from "./results/data";
import SearchResultsError from "./results/error";
import Searching from "./results/searching";
import SearchSuggestions from "./suggestions";

export enum DisplayState {
  Suggestions = "suggestions",
  Searching = "searching",
  Results = "results",
  Error = "error",
}

export default function SearchResults() {
  const { setRouting } = useSearchRouting();
  const { normalizedQuery, debouncedQuery, searchRefreshId } = useSearchQuery();
  const { data, error } = useConversationsSearch(normalizedQuery, debouncedQuery, searchRefreshId);

  // Determine what we should be displaying
  const displayState = useMemo(() => {
    if (normalizedQuery.length <= 0) {
      // No query, show suggestions
      return DisplayState.Suggestions;
    } else if (normalizedQuery != debouncedQuery) {
      // We're waiting on the new query to be debounced
      return DisplayState.Searching;
    } else if (error != null) {
      // We have a valid error and it's been debounced
      return DisplayState.Error;
    } else if (data != null) {
      // We have data and it's been debounced
      return DisplayState.Results;
    } else {
      // We're waiting for search results to return
      return DisplayState.Searching;
    }
  }, [normalizedQuery, debouncedQuery, data, error]);

  // Update the routing for the displayed results
  useEffect(() => {
    if (displayState == DisplayState.Results) {
      setRouting(data?.routing);
    } else {
      setRouting(undefined);
    }
  }, [data, displayState, setRouting]);

  // Get animation configuration
  const { mode, transition } = useAnimationMode(displayState);

  return (
    <div className="dewey--search--results">
      <AnimatePresence
        mode={mode}
        custom={transition}
      >
        {displayState === DisplayState.Searching && <Searching key="searching" />}
        {displayState === DisplayState.Suggestions && <SearchSuggestions key="suggestions" />}
        {displayState === DisplayState.Results && (
          <SearchResultsData
            key="data"
            data={data}
          />
        )}
        {displayState === DisplayState.Error && <SearchResultsError key="error" />}
      </AnimatePresence>
    </div>
  );
}
